import { Injectable, inject } from '@angular/core';
import { MapPosition } from './models/map-position.model';
import { Subject } from 'rxjs';
import { tap, debounceTime, takeUntil, take, withLatestFrom } from 'rxjs/operators';
import { MapEventsService } from '../services/map-events.service';
import { Store } from '@ngrx/store';
import * as fromCop from 'src/app/modules/cop/store/cop';
import { LoggerService } from '../../core/services/logger/logger.service';
import { Logger, LogLevel } from '../../core/services/logger/logger.model';
import { MapDataWorkerService } from 'src/app/modules/cop/web-workers/client/map-data-worker.service';
import { MapDrawingStateService } from './map-drawing-state.service';
import { MapWallDisplayFacade } from '@app/features/operating-picture/map-wall-display/store/map-wall-display.facade';

@Injectable({
  providedIn: 'root'
})
export class MapPositionService {
  logger: Logger;
  destroy$: Subject<void> = new Subject();
  position: MapPosition = {
    northEast: { lat: 0, lng: 0 },
    southWest: { lat: 0, lng: 0 },
    center: { lat: 0, lng: 0 },
    zoomLevel: 0
  };

  private mapWallDisplay = inject(MapWallDisplayFacade);

  constructor(
    private mapEvents: MapEventsService,
    private store$: Store,
    private loggerService: LoggerService,
    private mapDataWorkerService: MapDataWorkerService,
    private mapDrawingStateService: MapDrawingStateService
  ) {
    this.logger = this.loggerService.getLogger('MapPositionService');

    this.mapEvents.mapBoundsChanged$.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(visibleLayerUIds => {
      this.onBoundsChanged(visibleLayerUIds);
    });

    this.mapEvents.mapZoomChanged$.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => {
      this.onZoomChanged();
    });
  }

  onBoundsChanged(visibleLayerUIds: string[]) {
    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'onBoundsChanged()');

    if (!this.mapDrawingStateService.googleMap) {
      stopwatch.stop();
      return;
    }

    const bounds = this.mapDrawingStateService.googleMap.getBounds();

    if (!!!bounds) {
      stopwatch.stop();
      return;
    }

    const northEast = bounds.getNorthEast();
    this.position.northEast.lat = northEast.lat();
    this.position.northEast.lng = northEast.lng();

    const southWest = bounds.getSouthWest();
    this.position.southWest.lat = southWest.lat();
    this.position.southWest.lng = southWest.lng();

    const center = bounds.getCenter();
    this.position.center.lat = center.lat();
    this.position.center.lng = center.lng();

    this.position.zoomLevel = this.mapDrawingStateService.googleMap.getZoom();
    this.notifyPositionChanged();

    // send a message to the webworker and tell it that the map has changed
    // locations. this may trigger updates for layers like a requery for flight
    // asset layers since they are bounded to the map position.
    this.mapDataWorkerService.setMapPositionAndZoom(this.position, visibleLayerUIds);

    stopwatch.stop();
  }

  onZoomChanged() {
    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'onZoomChanged()');

    if (!this.mapDrawingStateService.googleMap) {
      stopwatch.stop();
      return;
    }

    const zoomLevel = this.mapDrawingStateService.googleMap.getZoom();

    if (zoomLevel !== undefined) {
      this.position.zoomLevel = zoomLevel;
      this.notifyPositionChanged();
    }

    stopwatch.stop();
  }

  notifyPositionChanged(): void {
    if (this.mapWallDisplay.isActive() && !this.mapWallDisplay.isMapInWorkbenchMode()) {
      return;
    }

    this.store$.dispatch(
      fromCop.mapMapPositionChanged({
        mapPosition: {
          northEast: {
            lat: this.position.northEast.lat,
            lng: this.position.northEast.lng
          },
          southWest: {
            lat: this.position.southWest.lat,
            lng: this.position.southWest.lng
          },
          center: {
            lat: this.position.center.lat,
            lng: this.position.center.lng
          },
          zoomLevel: this.position.zoomLevel
        }
      })
    );
  }
}
